import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useRecoilState } from 'recoil';
import {
  Table,
  Select,
  Input,
  Collapse,
} from 'antd';
import { notificationsAtom, userPrivilagesAtom } from '../../../atoms/Atoms';
import MainPage from '../../shared-components/main-page/MainPage';
import { H1 } from '../../shared-components/typography/Title';
import api from '../../../api/api';
import { thousand } from '../../../functions';

function Comparisons() {
  const { t } = useTranslation();
  const { Option } = Select;
  const { Panel } = Collapse;
  const sortFilters = [
    { name: `${t('clover_sales_count')} ${t('desc')}`, type: 0 },
    { name: `${t('clover_sales_count')} ${t('asc')}`, type: 1 },
    { name: `${t('grocefy_sales_count')} ${t('desc')}`, type: 2 },
    { name: `${t('grocefy_sales_count')} ${t('asc')}`, type: 3 },
    { name: `${t('grocefy_name')} Z-A`, type: 4 },
    { name: `${t('grocefy_name')} A-Z`, type: 5 },
    { name: `${t('clover_name')} Z-A`, type: 6 },
    { name: `${t('clover_name')} A-Z`, type: 7 },
    { name: `${t('price')} ${t('desc')}`, type: 8 },
    { name: `${t('price')} ${t('asc')}`, type: 9 },
    { name: `${t('clover_price')} ${t('desc')}`, type: 10 },
    { name: `${t('clover_price')} ${t('asc')}`, type: 11 },
    { name: `${t('clover_cost')} ${t('desc')}`, type: 12 },
    { name: `${t('clover_cost')} ${t('asc')}`, type: 13 },
  ];
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const userPrivilages = useRecoilValue(userPrivilagesAtom);
  const [loading, setLoading] = useState(false);
  const [lastSearchTerm, setLastSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loadingStores, setLoadingStores] = useState(false);
  const [stores, setStores] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCloverCategories, setLoadingCloverCategories] = useState(false);
  const [cloverCategories, setCloverCategories] = useState([]);
  const [searchResult, setSearchResult] = useState(null);
  const [selectedSortFilter, setSelectedSortFilter] = useState(0);
  const [selectedStore, setSelectedStore] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCloverCategory, setSelectedCloverCategory] = useState('');
  const [advancedSearch, setAdvancedSearch] = useState(false);
  const [hasMatch, setHasMatch] = useState(null);
  const [priceMatch, setPriceMatch] = useState(null);
  const [categoryMatch, setCategoryMatch] = useState(null);
  const [taxRateMatch, setTaxRateMatch] = useState(null);
  const [hasTax, setHasTax] = useState(null);

  function getStores() {
    setLoadingStores(true);
    api.get('comparison/stores')
      .then((response) => {
        setLoadingStores(false);
        if (response.data.success) {
          setStores(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoadingStores(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      })
  }

  function getCategories() {
    setLoadingCategories(true);
    api.get('comparison/cateogries')
      .then((response) => {
        setLoadingCategories(false);
        if (response.data.success) {
          setCategories(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoadingCategories(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      })
  }

  function getCloverCategories() {
    setLoadingCloverCategories(true);
    api.get('comparison/clover/cateogries')
      .then((response) => {
        setLoadingCloverCategories(false);
        if (response.data.success) {
          setCloverCategories(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoadingCloverCategories(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      })
  }

  function search(value, page) {
    setLastSearchTerm(value);
    setCurrentPage(page);
    setLoading(true);
    api
      .post(
        'comparison/clover/grocefy',
        {
          Query: value,
          Size: pageSize,
          Page: page,
          Sort: selectedSortFilter,
          BusinessId: selectedStore,
          Category: selectedCategory,
          CloverCategory: selectedCloverCategory,
          HasMatch: advancedSearch ? hasMatch : null,
          PriceMatch: advancedSearch ? priceMatch : null,
          CategoryMatch: advancedSearch ? categoryMatch : null,
          TaxRateMatch: advancedSearch ? taxRateMatch : null,
          HasTax: advancedSearch ? hasTax : null,
        }
      )
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setSearchResult(response.data.data);
        } else {
          setSearchResult({
            ...searchResult,
            comparisons: []
          });
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  function onSearch(value) {
    search(value, 0);
  }

  const onTableChange = (page, size) => {
    if (page - 1 !== currentPage) {
      setCurrentPage(page - 1);
      search(lastSearchTerm, page - 1);
    } else {
      setPageSize(size);
    }
  };

  useEffect(() => {
    if (!advancedSearch) {
      search(lastSearchTerm, 0);  
    }
  }, [advancedSearch]);

  useEffect(() => {
    search(lastSearchTerm, 0);
  }, [selectedSortFilter, hasMatch, priceMatch, categoryMatch,
      taxRateMatch, selectedCategory, selectedCloverCategory,
      selectedStore, pageSize, hasTax]);

  useEffect(() => {
    setSelectedSortFilter(0);
    setSelectedStore('');
    setSelectedCategory('');
    setSelectedCloverCategory('');
    getStores();
    getCloverCategories();
    getCategories();
    search('', 0);
  }, []);

  return (
    <MainPage>
      <div className="flex mt-4">
        <H1 text={t('data_comparisons')} />
      </div>
      <div className="lg:flex items-center justify-center">
        <Input.Search
          placeholder={`${t('search')}...`}
          allowClear
          className="mt-4"
          enterButton={t('search')}
          size="large"
          onClear={() => onSearch('')}
          onSearch={(val) => onSearch(val)}
        />
        <div className="grid grid-cols-2 md:grid-cols-4 md:space-x-2 lg:flex">
          <Select
            loading={loadingStores}
            className="h-10 col-span-1 mt-2 lg:mt-4 lg:w-48 mr-1 md:mr-0 ml-0 lg:ml-2"
            value={selectedStore}
            onChange={(value) => setSelectedStore(value)}
          >
            <Option
              key="nostore"
              value=""
              className="text-sm flex items-center text-start"
            >
              {t('any_store')}
            </Option>
            {_.map(stores, (store) => (
              <Option
                key={store.id}
                value={store.id}
                className="text-sm flex items-center text-start"
              >
                {store.name}
              </Option>
            ))}
          </Select>
          <Select
            loading={loadingCategories}
            className="h-10 col-span-1 mt-2 lg:mt-4 lg:w-48 ml-1 md:ml-0"
            value={selectedCategory}
            onChange={(value) => setSelectedCategory(value)}
          >
            <Option
              key="nocategory"
              value=""
              className="text-sm flex items-center text-start"
            >
              {t('any_category')}
            </Option>
            {_.map(categories, (category) => (
              <Option
                key={category ?? '[no_data]'}
                value={category ?? '[no_data]'}
                className="text-sm flex items-center text-start"
              >
                {category ?? t('[no_data]')}
              </Option>
            ))}
          </Select>
          <Select
            loading={loadingCloverCategories}
            className="h-10 col-span-1 mt-2 lg:mt-4 lg:w-48 mr-1 md:mr-0"
            value={selectedCloverCategory}
            onChange={(value) => setSelectedCloverCategory(value)}
          >
            <Option
              key="noclovercategory"
              value=""
              className="text-sm flex items-center text-start"
            >
              {t('any_clover_category')}
            </Option>
            {_.map(cloverCategories, (category) => (
              <Option
                key={category ?? '[no_data]'}
                value={category ?? '[no_data]'}
                className="text-sm flex items-center text-start"
              >
                {category ?? t('[no_data]')}
              </Option>
            ))}
          </Select>
          <Select
            className="h-10 col-span-1 mt-2 lg:mt-4 lg:w-48 ml-1 md:ml-0"
            value={selectedSortFilter}
            onChange={(value) => setSelectedSortFilter(value)}
          >
            {_.map(sortFilters, (filter) => (
              <Option
                key={filter.type}
                value={filter.type}
                className="text-sm flex items-center text-start"
              >
                {filter.name}
              </Option>
            ))}
          </Select>
        </div>
      </div>
      <Collapse
        bordered={false}
        activeKey={advancedSearch}
        onChange={(obj) => setAdvancedSearch(obj.length > 0 ? '1' : null)}
      >
        <Panel key="1" header={t('advanced_search')}>
          <div className="grid grid-cols-2 mt-2 gap-2 lg:grid-cols-4">
            <div className="col-md-4 lg:flex">
              <p className="lg:mt-3 lg:mr-2">{t('upc_match')}</p>
              <Select
                className="h-10 mt-1 w-full lg:w-48"
                value={hasMatch}    
                onChange={(value) => setHasMatch(value)}       
              >
                <Option
                  key="nomatch"
                  value={null}
                  className="text-sm flex items-center text-start"             
                >
                  {t('any')}
                </Option>
                <Option
                  key="hasmatchtrue"
                  value={true}
                  className="text-sm flex items-center text-start"
                >
                  {t('yes')}
                </Option>
                <Option
                  key="hasmatchfalse"
                  value={false}
                  className="text-sm flex items-center text-start"
                >
                  {t('no')}
                </Option>
              </Select>
            </div>
            <div className="col-md-4 lg:flex">
              <p className="lg:mt-3 lg:mr-2">{t('price_match')}</p>
              <Select
                className="h-10 mt-1 w-full lg:w-48"
                value={priceMatch}    
                onChange={(value) => setPriceMatch(value)}       
              >
                <Option
                  key="nopricematch"
                  value={null}
                  className="text-sm flex items-center text-start"             
                >
                  {t('any')}
                </Option>
                <Option
                  key="haspricematchtrue"
                  value={true}
                  className="text-sm flex items-center text-start"
                >
                  {t('yes')}
                </Option>
                <Option
                  key="haspricematchfalse"
                  value={false}
                  className="text-sm flex items-center text-start"
                >
                  {t('no')}
                </Option>
              </Select>
            </div>
            <div className="col-md-4 lg:flex">
              <p className="lg:mt-3 lg:mr-2">{t('has_tax')}</p>
              <Select
                className="h-10 mt-1 w-full lg:w-48"
                value={hasTax}
                onChange={(value) => setHasTax(value)}       
              >
                <Option
                  key="anytax"
                  value={null}
                  className="text-sm flex items-center text-start"             
                >
                  {t('any')}
                </Option>
                <Option
                  key="hastax"
                  value={true}
                  className="text-sm flex items-center text-start"
                >
                  {t('yes')}
                </Option>
                <Option
                  key="hastaxfalse"
                  value={false}
                  className="text-sm flex items-center text-start"
                >
                  {t('no')}
                </Option>
              </Select>
            </div>
            <div className="col-md-4 lg:flex">
              <p className="lg:mt-3 lg:mr-2">{t('tax_match')}</p>
              <Select
                className="h-10 mt-1 w-full lg:w-48"
                value={taxRateMatch}    
                onChange={(value) => setTaxRateMatch(value)}       
              >
                <Option
                  key="notaxmatch"
                  value={null}
                  className="text-sm flex items-center text-start"             
                >
                  {t('any')}
                </Option>
                <Option
                  key="hastaxmatchtrue"
                  value={true}
                  className="text-sm flex items-center text-start"
                >
                  {t('yes')}
                </Option>
                <Option
                  key="hastaxmatchfalse"
                  value={false}
                  className="text-sm flex items-center text-start"
                >
                  {t('no')}
                </Option>
              </Select>
            </div>
            <div className="col-md-4 lg:flex">
              <p className="lg:mt-3 lg:mr-2">{t('category_match')}</p>
              <Select
                className="h-10 mt-1 w-full lg:w-48"
                value={categoryMatch}    
                onChange={(value) => setCategoryMatch(value)}       
              >
                <Option
                  key="nocategorymatch"
                  value={null}
                  className="text-sm flex items-center text-start"             
                >
                  {t('any')}
                </Option>
                <Option
                  key="hascategorymatchtrue"
                  value={true}
                  className="text-sm flex items-center text-start"
                >
                  {t('yes')}
                </Option>
                <Option
                  key="hascategorymatchfalse"
                  value={false}
                  className="text-sm flex items-center text-start"
                >
                  {t('no')}
                </Option>
              </Select>
            </div>
            {/* <Checkbox
              checked={hasMatch}
              onChange={(value) => setHasMatch(value.target.checked)}
            >
              {t('has_match')}
            </Checkbox> */}
            {/* <Checkbox
              checked={priceMatch}
              onChange={(value) => setPriceMatch(value.target.checked)}
            >
              {t('price_match')}
            </Checkbox>
            <Checkbox
              checked={categoryMatch}
              onChange={(value) => setCategoryMatch(value.target.checked)}
            >
              {t('category_match')}
            </Checkbox>
            <Checkbox
              checked={taxRateMatch}
              onChange={(value) => setTaxRateMatch(value.target.checked)}
            >
              {t('tax_rate_match')}
            </Checkbox> */}
          </div>
        </Panel>
      </Collapse>
      <div className="mt-4">{t('found_in_search')}: {searchResult?.comparisons ? thousand(searchResult?.total) : 0}</div>
      <Table
        locale={{
          emptyText: t('no_data')
        }}
        bordered
        loading={loading}
        pagination={{
          pageSize,
          showSizeChanger: true,
          current: currentPage + 1,
          total: searchResult?.total,
          onChange: onTableChange
        }}
        dataSource={searchResult?.comparisons}
        columns={[
          {
            title: t('upc_match'),
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <span>{row.hasMatch}</span>
            ),
          },
          {
            title: t('price_match'),
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <span>{row.priceMatch}</span>
            ),
          },
          {
            title: t('tax_match'),
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <span>{row.taxRateMatch}</span>
            ),
          },
          {
            title: t('category_match'),
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <span>{row.categoryMatch}</span>
            ),
          },
          {
            title: t('clover_code'),
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <span>{row.cloverCode}</span>
            ),
          },
          {
            title: t('clover_name'),
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <span>{row.cloverName}</span>
            ),
          }, {
            title: t('clover_price'),
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <span>${row.cloverPrice}</span>
            ),
          },
            {
              title: t('clover_tax'),
              align: 'left',
              className: 'text-sm',
              render: (row) => (
                <span>{row.cloverTotalTax}</span>
              ),
            }, {
            title: t('grocefy_upc'),
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <span>{row.grocefyUpc}</span>
            ),
          },
          {
            title: t('brand'),
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <span>{row.brand}</span>
            ),
          },
          {
            title: t('grocefy_name'),
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <span>{row.name}</span>
            ),
          },
          {
            title: t('description'),
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <span>{row.description}</span>
            ),
          },
          {
            title: t('grocefy_price'),
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <span>${row.price}</span>
            ),
          },
          {
            title: t('clover_sales_Count'),
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <span>{thousand(row.cloverSalesCount)}</span>
            ),
          }
        ]}
      />
    </MainPage>
  );
}

export default Comparisons;
